import React from 'react';

// Color mapping for each specialization
const SPEC_COLORS = {
  nursing: { bg: 'bg-rose-500', hover: 'hover:bg-rose-600', shadow: 'shadow-rose-500/30', gradient: 'from-rose-500 to-rose-600' },
  education: { bg: 'bg-amber-500', hover: 'hover:bg-amber-600', shadow: 'shadow-amber-500/30', gradient: 'from-amber-500 to-amber-600' },
  law: { bg: 'bg-indigo-500', hover: 'hover:bg-indigo-600', shadow: 'shadow-indigo-500/30', gradient: 'from-indigo-500 to-indigo-600' },
  medicine: { bg: 'bg-emerald-500', hover: 'hover:bg-emerald-600', shadow: 'shadow-emerald-500/30', gradient: 'from-emerald-500 to-emerald-600' },
  dentistry: { bg: 'bg-cyan-500', hover: 'hover:bg-cyan-600', shadow: 'shadow-cyan-500/30', gradient: 'from-cyan-500 to-cyan-600' },
  pharmacy: { bg: 'bg-purple-500', hover: 'hover:bg-purple-600', shadow: 'shadow-purple-500/30', gradient: 'from-purple-500 to-purple-600' },
};

const DEFAULT_COLOR = { bg: 'bg-teal-500', hover: 'hover:bg-teal-600', shadow: 'shadow-teal-500/30', gradient: 'from-teal-500 to-teal-600' };

// Specializations that are not yet ready for student release
const COMING_SOON_SPECS = new Set();

/**
 * SpecializationGrid - Grid of specialization buttons with unique colors
 */
function SpecializationGrid({ 
  specializations, 
  questionCounts, 
  videoCounts,
  onSpecSelect 
}) {
  const mainSpecs = specializations.filter(s => s.id !== 'gov-exams');

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
      {mainSpecs.map((spec) => {
        const stats = questionCounts[spec.id] || {};
        const questionCount = stats.questions || spec.question_count || 0;
        const courseCount = spec.courses?.length || 0;
        const colors = SPEC_COLORS[spec.id] || DEFAULT_COLOR;
        const isComingSoon = COMING_SOON_SPECS.has(spec.id);

        return (
          <button
            key={spec.id}
            onClick={() => { if (!isComingSoon) onSpecSelect(spec); }}
            disabled={isComingSoon}
            className={`bg-gradient-to-br ${colors.gradient} text-white px-4 py-5 rounded-2xl font-bold text-lg shadow-lg ${colors.shadow} transition-all duration-300 flex flex-col items-center gap-2 relative overflow-hidden group ${isComingSoon ? 'opacity-60 cursor-not-allowed grayscale-[35%]' : 'hover:shadow-xl active:scale-95 hover:-translate-y-1'}`}
            data-testid={`spec-button-${spec.id}`}
            aria-disabled={isComingSoon}
          >
            {isComingSoon && (
              <span
                className="absolute top-2 right-2 z-20 bg-amber-400 text-amber-900 text-[10px] font-black px-2 py-0.5 rounded-full shadow-md tracking-wide"
                data-testid={`spec-coming-soon-${spec.id}`}
              >
                בקרוב
              </span>
            )}
            <div className={`absolute inset-0 bg-white/0 transition-colors duration-300 ${isComingSoon ? '' : 'group-hover:bg-white/10'}`} />
            <span className="text-2xl relative z-10">{spec.icon}</span>
            <span className="relative z-10 text-base">{spec.name}</span>
            <div className="text-xs opacity-80 relative z-10 flex flex-col items-center">
              {isComingSoon ? (
                <span className="font-bold">בפיתוח</span>
              ) : (
                <>
                  {questionCount > 0 && <span>{questionCount.toLocaleString()} שאלות</span>}
                  {courseCount > 0 && <span>{courseCount} קורסים</span>}
                </>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/**
 * LicenseExamsSection - Expandable section for license exams
 */
function LicenseExamsSection({ 
  specializations, 
  questionCounts, 
  showLicenseExams, 
  onToggle,
  onExamSelect,
  onStartQuiz 
}) {
  const govExamsSpec = specializations.find(s => s.id === 'gov-exams');
  
  if (!govExamsSpec) return null;

  return (
    <div className="mb-8">
      <button
        onClick={onToggle}
        className="w-full bg-gradient-to-l from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white py-4 px-6 rounded-2xl shadow-lg shadow-blue-500/20 hover:shadow-xl transition-all duration-300 flex items-center justify-between"
        data-testid="license-exams-toggle"
      >
        <div className={`text-xl transition-transform duration-300 ${showLicenseExams ? 'rotate-180' : ''}`}>
          ▼
        </div>
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold">מבחני רישוי ממשלתיים</h2>
          <div className="text-2xl">🎓</div>
        </div>
      </button>
      
      {showLicenseExams && (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 animate-slide-up">
          {(govExamsSpec.exams || []).map((exam) => {
            const stats = questionCounts[exam.id] || {};
            const questionCount = stats.questions || exam.question_count || 0;
            const isComingSoon = false;

            return (
              <button
                key={exam.id}
                onClick={() => { if (!isComingSoon) onExamSelect(exam, govExamsSpec); }}
                disabled={isComingSoon}
                className={`bg-white border border-slate-200 p-5 rounded-2xl shadow-sm transition-all duration-300 text-center group relative ${isComingSoon ? 'opacity-60 cursor-not-allowed grayscale-[35%]' : 'hover:border-blue-400 hover:bg-blue-50 hover:shadow-lg active:scale-95 hover:-translate-y-1'}`}
                data-testid={`gov-exam-${exam.id}`}
                aria-disabled={isComingSoon}
              >
                {isComingSoon && (
                  <span
                    className="absolute top-2 right-2 z-20 bg-amber-400 text-amber-900 text-[10px] font-black px-2 py-0.5 rounded-full shadow-md tracking-wide"
                    data-testid={`gov-exam-coming-soon-${exam.id}`}
                  >
                    בקרוב
                  </span>
                )}
                <div className={`text-4xl mb-3 transition-transform duration-300 ${isComingSoon ? '' : 'group-hover:scale-110'}`}>{exam.icon}</div>
                <div className="font-bold text-sm text-slate-800">{exam.name}</div>
                {isComingSoon ? (
                  <div className="text-xs text-amber-700 font-medium mt-2">בפיתוח</div>
                ) : (
                  <>
                    {questionCount > 0 && (
                      <div className="text-xs text-slate-500 mt-1">{questionCount.toLocaleString()} שאלות</div>
                    )}
                    {['medical-gov', 'nursing-gov', 'dental-gov', 'pharmacy-gov'].includes(exam.id) && (
                      <div className="text-xs text-blue-600 font-medium mt-2 bg-blue-50 rounded-full px-2 py-0.5 inline-block">סימולציה מלאה</div>
                    )}
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export { SpecializationGrid, LicenseExamsSection };
